import axios, { AxiosError, type AxiosResponse, type InternalAxiosRequestConfig } from "axios";
import { isRoot2FAResponse } from "@/features/auth/services/login.dto";
import { useAuthStore } from "@/features/auth/store/useAuthStore";
import { refreshTokenManager } from "@/features/auth/utils/refresh-token-manager";
import { tokenManager } from "@/features/auth/utils/token-manager";
import { camelToSnake, snakeToCamel } from "@/utils/case-transform";
import { logger } from "@/utils/logger";

declare module "axios" {
  export interface InternalAxiosRequestConfig {
    /**
     *  Designated for retry mechanism
     *  If the request is retried, the value is set to true
     */
    _retry?: boolean;
  }
}

const baseURL = import.meta.env.VITE_BASE_URL;
const axiosInstance = axios.create({ baseURL });
export const axiosRefreshInstance = axios.create({ baseURL });

axiosInstance.interceptors.request.use(
  (config) => enhancedRequestSuccess(config),
  (error) => enhancedRequestError(error),
);

axiosInstance.interceptors.response.use(
  (response) => enhancedSuccessResponse(response),
  (error) => enhancedErrorResponse(error),
);

const enhancedRequestSuccess = (config: InternalAxiosRequestConfig) => {
  const transferredData = camelToSnake(config.data);
  const transferredParams = camelToSnake(config.params);

  const apiKey = useAuthStore.getState().apiKey;
  const accessToken = tokenManager.accessToken;

  config.headers = config.headers || {};

  if (apiKey) config.headers["X-API-Key"] = apiKey;
  if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;

  return { ...config, data: transferredData, params: transferredParams };
};

const enhancedRequestError = (error: AxiosError) => {
  logger.error("Request error:", error);
  return Promise.reject(error);
};

const enhancedSuccessResponse = (response: AxiosResponse): AxiosResponse => {
  const transferredData = snakeToCamel(response.data);

  if (isRoot2FAResponse(transferredData)) {
    useAuthStore.getState().setApiKey(transferredData.data.apiKey);
  }

  return { ...response, data: transferredData };
};

const enhancedErrorResponse = async (error: AxiosError) => {
  const originalRequest = error.config;
  const authStore = useAuthStore.getState();

  /**
   *  If the request is unauthorized, try to refresh the token
   *  If the refresh token is invalid, logout the user
   */
  if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
    originalRequest._retry = true;
    try {
      const refreshToken = tokenManager.refreshToken;
      if (refreshToken) {
        const response = await refreshTokenManager.refresh();
        const { accessToken, refreshToken } = response;

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        }

        tokenManager.saveTokens(accessToken, refreshToken);

        return axiosInstance(originalRequest);
      }
    } catch (refreshError) {
      logger.error("Refresh token error:", refreshError);

      authStore.logout();
      window.location.replace("/auth/login");

      return Promise.reject({ ...error });
    }
  }

  /**
   *  If the request is forbidden, logout the user
   */
  if (error.response?.status === 403) {
    authStore.logout();
    return Promise.reject({ ...error.response });
  }

  if (error.response) return Promise.reject({ ...error.response });

  return Promise.reject({ ...error });
};

export default axiosInstance;
